// Modified DashboardNavigation.js - Added Help option to Settings menu
import React, { useState } from 'react';
import { 
  User, Link as LinkIcon, Image, Palette, Layout, 
  Crown, LogOut, Ghost, Settings, HelpCircle,
  AlertTriangle, UserMinus, ChevronDown, ChevronUp,
  Link2, CreditCard
} from 'lucide-react';
import styles from './Dashboard.module.css';

const DashboardNavigation = ({
  activeSection,
  setActiveSection,
  userData,
  handleUpgrade,
  handleProFeatureClick, 
  handleSignOut,
  menuOpen,
  isProUser,
  openHelp // New prop to handle opening help
}) => {
  const [shakeProButton, setShakeProButton] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const handleGhostClick = () => {
    if (!isProUser) {
      setShakeProButton(true);
      // Reset shake after animation completes
      setTimeout(() => setShakeProButton(false), 820);
      handleProFeatureClick();
    }
    setActiveSection('ghost');
  };

  // Handler for link shortener click
  const handleShortenerClick = () => {
    if (!isProUser) {
      setShakeProButton(true);
      // Reset shake after animation completes
      setTimeout(() => setShakeProButton(false), 820);
      handleProFeatureClick();
    }
    setActiveSection('shortener');
  };

  const toggleSettings = () => {
    setSettingsExpanded(!settingsExpanded);
  };

  return (
    <nav className={`${styles.nav} ${menuOpen ? styles.navOpen : ''}`}>
      <div className={styles.userInfo}>
        <div className={styles.avatar}>
          {userData.photoURL ? (
            <img 
              src={userData.photoURL} 
              alt="Profile" 
              className={styles.avatarImage}
              onError={(e) => {
                e.target.src = '';
              }}
            />
          ) : (
            <div className={styles.initials}>
              {userData.profile?.displayName
                ? userData.profile.displayName.charAt(0).toUpperCase()
                : (userData.username ? userData.username.charAt(0).toUpperCase() : '')
              }
            </div>
          )}
        </div>
        <h2>{userData.profile?.displayName || userData.username}</h2>
        
        {/* Show subscription badge if Pro */}
        {isProUser && (
          <div className={styles.proUserBadge}>
            <Crown size={14} className={styles.menuCrown} />
            <span>PRO</span>
          </div>
        )}
      </div>
      <ul>
        <li 
          className={activeSection === 'details' ? styles.active : ''}
          onClick={() => setActiveSection('details')}
        >
          <User size={20} /> Details
        </li>
        <li 
          className={activeSection === 'links' ? styles.active : ''}
          onClick={() => setActiveSection('links')}
        >
          <LinkIcon size={20} /> Edit Links
        </li>
        <li 
          className={activeSection === 'icons' ? styles.active : ''}
          onClick={() => setActiveSection('icons')}
        >
          <Image size={20} /> Edit Icons
        </li>
        <li 
          className={activeSection === 'palette' ? styles.active : ''}
          onClick={() => setActiveSection('palette')}
        >
          <Palette size={20} /> Edit Palette
        </li>
        <li 
          className={activeSection === 'theme' ? styles.active : ''}
          onClick={() => setActiveSection('theme')}
        >
          <Layout size={20} /> Edit Theme
        </li>
        {/* Ghost Mode menu item */}
        <li 
          className={`${activeSection === 'ghost' ? styles.active : ''} ${styles.ghostModeItem}`}
          onClick={handleGhostClick}
        >
          <Ghost size={20} /> Ghost Mode
          {!isProUser && (
            <div className={styles.proBadge}>
              <Crown size={14} className={styles.menuCrown} />
              <span>PRO</span>
            </div>
          )}
        </li>
        
        {/* Link Shortener menu item */}
        <li 
          className={`${activeSection === 'shortener' ? styles.active : ''} ${styles.shortenerItem}`}
          onClick={handleShortenerClick}
        >
          <Link2 size={20} /> Link Shortener
          {!isProUser && (
            <div className={styles.proBadge}>
              <Crown size={14} className={styles.menuCrown} />
              <span>PRO</span>
            </div>
          )}
        </li>
        
        {/* Subscription item - accessible to all users */}
        <li 
          className={`${activeSection === 'subscription' ? styles.active : ''}`}
          onClick={() => setActiveSection('subscription')}
        >
          <CreditCard size={20} /> Subscription
          {isProUser && (
            <div className={styles.proBadge} style={{ background: 'rgba(255, 215, 0, 0.2)' }}>
              <Crown size={14} className={styles.menuCrown} />
              <span>PRO</span>
            </div>
          )}
        </li>
        
        {/* Settings Menu Item with Expandable Sub-menu */}
        <li 
          className={styles.settingsItem}
          onClick={toggleSettings}
        >
          <div className={styles.settingsHeader}>
            <div className={styles.settingsIcon}>
              <Settings size={20} /> Settings
            </div>
            {settingsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </li>
        
        {/* Settings Sub-menu Items */}
        {settingsExpanded && (
          <div className={styles.settingsSubmenu}>
            {/* New Help menu item inside settings */}
            <li 
              className={`${styles.submenuItem}`}
              onClick={openHelp}
            >
              <HelpCircle size={18} /> Help & Tips
            </li>
            
            <li 
              className={`${activeSection === 'reportProblem' ? styles.active : ''} ${styles.submenuItem}`}
              onClick={() => setActiveSection('reportProblem')}
            >
              <AlertTriangle size={18} /> Report a Problem
            </li>
            <li 
              className={`${activeSection === 'cancelAccount' ? styles.active : ''} ${styles.submenuItem}`}
              onClick={() => setActiveSection('cancelAccount')}
            >
              <UserMinus size={18} /> Manage Account
            </li>
          </div>
        )}

        {/* Don't show the upgrade button to Pro users */}
        {!isProUser && (
          <li 
            className={`${styles.proButton} ${shakeProButton ? styles.shake : ''}`} 
            onClick={handleUpgrade}
          >
            <Crown size={20} /> Go Pro
          </li>
        )}
        
        <li className={styles.signOut} onClick={handleSignOut}>
          <LogOut size={20} /> Sign Out
        </li>
      </ul>
    </nav>
  );
};

export default DashboardNavigation;